import { useState, useCallback, useEffect } from 'react';
import { Project, Component } from '../types';
import { checkApiHealth } from '../services/api';
import { projectsApi, componentsApi } from '../services/api';

interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface UseApiReturn<T> extends ApiState<T> {
  fetchData: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

// Helper function to determine if a string is an ID or name
const isProjectId = (identifier: string): boolean => {
  // Check if it looks like an ID (contains project- prefix or is a UUID-like format)
  return identifier.startsWith('project-') || /^[a-f0-9-]{36}$/i.test(identifier);
};

// Hook for projects API
export const useProjects = (): UseApiReturn<Project[]> => {
  const [state, setState] = useState<ApiState<Project[]>>({
    data: null,
    loading: false,
    error: null,
  });

  const fetchData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const projects = await projectsApi.getAll();
      setState({ data: projects, loading: false, error: null });
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        loading: false, 
        error: error instanceof Error ? error.message : 'Failed to fetch projects' 
      }));
    }
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  // Load data on mount - only run once
  useEffect(() => {
    fetchData();
  }, []); // Remove fetchData from dependencies to prevent infinite loop

  return { ...state, fetchData, clearError, reset };
};

// Hook for single project API - supports both ID and name
export const useProject = (projectIdentifier: string): UseApiReturn<Project> => {
  const [state, setState] = useState<ApiState<Project>>({
    data: null,
    loading: false,
    error: null,
  });

  const fetchData = useCallback(async () => {
    if (!projectIdentifier) return;
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      let project: Project;
      if (isProjectId(projectIdentifier)) {
        // Use ID-based lookup
        project = await projectsApi.getById(projectIdentifier);
      } else {
        // Use name-based lookup (legacy support)
        project = await projectsApi.getByName(projectIdentifier);
      }
      setState({ data: project, loading: false, error: null });
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        loading: false, 
        error: error instanceof Error ? error.message : 'Failed to fetch project' 
      }));
    }
  }, [projectIdentifier]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  // Load data when projectIdentifier changes
  useEffect(() => {
    if (projectIdentifier) {
      fetchData();
    }
  }, [projectIdentifier]); // Remove fetchData from dependencies to prevent infinite loop

  return { ...state, fetchData, clearError, reset };
};

// Hook for components API - supports both ID and name
export const useComponents = (projectIdentifier: string): UseApiReturn<Component[]> => {
  const [state, setState] = useState<ApiState<Component[]>>({
    data: null,
    loading: false,
    error: null,
  });

  const fetchData = useCallback(async () => {
    if (!projectIdentifier) return;
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      let components: Component[];
      if (isProjectId(projectIdentifier)) {
        // Use ID-based lookup
        components = await componentsApi.getByProjectId(projectIdentifier);
      } else {
        // Use name-based lookup (legacy support)
        components = await componentsApi.getByProject(projectIdentifier);
      }
      // Temporarily disable filtering for debugging
      // const validComponents = components.filter(component => {
      //   if (!component.projectName || component.projectName.trim() === '') {
      //     console.warn(`Component ${component.name} has no project name - skipping`);
      //     return false;
      //   }
      //   if (!component.projectId || component.projectId.trim() === '') {
      //     console.warn(`Component ${component.name} has no project ID - skipping`);
      //     return false;
      //   }
      //   return true;
      // });
      
      // if (validComponents.length !== components.length) {
      //   console.warn(`Filtered out ${components.length - validComponents.length} components with missing project information`);
      // }
      
      console.log('useComponents: Components from API:', components.length);
      console.log('useComponents: Sample components:', components.slice(0, 3).map(c => ({ name: c.name, project: c.projectName, status: c.status })));
      
      setState({ data: components, loading: false, error: null });
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        loading: false, 
        error: error instanceof Error ? error.message : 'Failed to fetch components' 
      }));
    }
  }, [projectIdentifier]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  // Load data when projectIdentifier changes
  useEffect(() => {
    if (projectIdentifier) {
      fetchData();
    }
  }, [projectIdentifier]); // Remove fetchData from dependencies to prevent infinite loop

  return { ...state, fetchData, clearError, reset };
};

// Hook for all components from all projects
export const useAllComponents = (): UseApiReturn<Component[]> => {
  const [state, setState] = useState<ApiState<Component[]>>({
    data: null,
    loading: false,
    error: null,
  });

  const fetchData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      // Use the new getAll method for components
      const allComponents = await componentsApi.getAll();
      
      // Temporarily disable filtering for debugging
      // const validComponents = allComponents.filter(component => {
      //   if (!component.projectName || component.projectName.trim() === '') {
      //     console.warn(`Component ${component.name} has no project name - skipping`);
      //     return false;
      //   }
      //   if (!component.projectId || component.projectId.trim() === '') {
      //     console.warn(`Component ${component.name} has no project ID - skipping`);
      //     return false;
      //   }
      //   return true;
      // });
      
      // if (validComponents.length !== allComponents.length) {
      //   console.warn(`Filtered out ${allComponents.length - validComponents.length} components with missing project information`);
      // }
      
      console.log('useAllComponents: All components from API:', allComponents.length);
      console.log('useAllComponents: Sample components:', allComponents.slice(0, 3).map(c => ({ 
        name: c.name, 
        project: c.projectName, 
        status: c.status,
        previewUrl: c.previewUrl,
        hasPreviewUrl: !!c.previewUrl
      })));
      console.log('useAllComponents: Raw component data (first component):', allComponents[0]);
      
      setState({ data: allComponents, loading: false, error: null });
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        loading: false, 
        error: error instanceof Error ? error.message : 'Failed to fetch all components' 
      }));
    }
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  // Load data on mount - only run once
  useEffect(() => {
    fetchData();
  }, []); // Remove fetchData from dependencies to prevent infinite loop

  return { ...state, fetchData, clearError, reset };
};

// Hook for API health check
export const useApiHealth = (): UseApiReturn<boolean> => {
  const [state, setState] = useState<ApiState<boolean>>({
    data: null,
    loading: false,
    error: null,
  });

  const fetchData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const isHealthy = await checkApiHealth();
      setState({ data: isHealthy, loading: false, error: null });
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        loading: false, 
        error: error instanceof Error ? error.message : 'Failed to check API health' 
      }));
    }
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return { ...state, fetchData, clearError, reset };
};
